import {
  planAlertEntitySchema,
  planAlertIdentifierSchema,
  scenarioAlertEntitySchema,
  scenarioAlertIdentifierSchema,
  scenarioChangesSchema,
  scenarioStatusSchema,
} from '@tempo-io/adaptive-planner-dto';
import {
  schemeEntitySchema,
  schemeIdentifierSchema,
  syncStatusEntitySchema,
  teamEntitySchema,
  teamIdentifierSchema,
  teamMembershipDeletedSchema,
  teamMembershipEntitySchema,
} from '@tempo-io/capacity-dto';
import { integrationEntitySchema, integrationIdentifierSchema } from '@tempo-io/item-store-dto';
import z from 'zod';
import { notificationTypeSchema, notificationVariablesSchema } from './notification-dto';

const workIntegrationSseBaseSchema = z.object({
  entityType: z.literal('work-integration'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const workIntegrationSseDtoSchema = z.discriminatedUnion('eventType', [
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: integrationEntitySchema,
  }),
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: integrationEntitySchema,
  }),
  workIntegrationSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: integrationIdentifierSchema,
  }),
]);

export const syncStatusSseDtoSchema = z.object({
  entityType: z.literal('sync-status'),
  entity: syncStatusEntitySchema,
  organizationId: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  timestamp: z.string(),
});

const teamSseBaseSchema = z.object({
  entityType: z.literal('team'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const teamSseDtoSchema = z.discriminatedUnion('eventType', [
  teamSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: teamEntitySchema,
  }),
  teamSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: teamIdentifierSchema,
  }),
]);

const teamMembershipSseBaseSchema = z.object({
  entityType: z.literal('team-membership'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const teamMembershipSseDtoSchema = z.discriminatedUnion('eventType', [
  teamMembershipSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: teamMembershipEntitySchema,
  }),
  teamMembershipSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: teamMembershipEntitySchema,
  }),
  teamMembershipSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: teamMembershipDeletedSchema,
  }),
]);

const holidaySchemeSseBaseSchema = z.object({
  entityType: z.literal('holiday-scheme'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const holidaySchemeSseDtoSchema = z.discriminatedUnion('eventType', [
  holidaySchemeSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: schemeEntitySchema,
  }),
  holidaySchemeSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: schemeIdentifierSchema,
  }),
]);

const workloadSchemeSseBaseSchema = z.object({
  entityType: z.literal('workload-scheme'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const workloadSchemeSseDtoSchema = z.discriminatedUnion('eventType', [
  workloadSchemeSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: schemeEntitySchema,
  }),
  workloadSchemeSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: schemeIdentifierSchema,
  }),
]);

export const membershipSseDtoSchema = z.object({
  entityType: z.literal('membership'),
  entity: z.object({
    id: z.string(),
    userId: z.string(),
    organizationId: z.string(),
    role: z.string().optional(),
  }),
  organizationId: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  timestamp: z.string(),
});

const notificationEntityBaseSchema = z.object({
  id: z.string(),
  userId: z.string(),
});

export const notificationSseEntitySchema = notificationEntityBaseSchema.extend({
  type: notificationTypeSchema,
  variables: notificationVariablesSchema,
});

export type NotificationSseEntity = z.infer<typeof notificationSseEntitySchema>;

export const notificationSseDtoSchema = z.object({
  entityType: z.literal('notification'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.literal('created'),
  entity: notificationSseEntitySchema,
});

const scenarioSseBaseSchema = z.object({
  entityType: z.literal('scenario'),
  organizationId: z.string(),
  timestamp: z.string(),
  loopId: z.number().int(),
});

export const scenarioSseDtoSchema = z.discriminatedUnion('eventType', [
  scenarioSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: z.object({
      id: z.number().int(),
      loopId: z.number().int(),
      status: scenarioStatusSchema,
      name: z.string(),
    }),
  }),
  scenarioSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: z.object({
      id: z.number().int(),
      loopId: z.number().int(),
      status: scenarioStatusSchema,
      name: z.string(),
    }),
  }),
  scenarioSseBaseSchema.extend({
    eventType: z.literal('updated_partial'),
    change: scenarioChangesSchema,
  }),
]);

export const slackWorkspaceSseDtoSchema = z.object({
  entityType: z.literal('slack-workspace'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({
    slackWorkspaceId: z.string(),
    connected: z.boolean(),
    slackWorkspaceName: z.string().nullish(),
  }),
});

const planAlertSseBaseSchema = z.object({
  entityType: z.literal('plan-alert'),
  organizationId: z.string(),
  timestamp: z.string(),
  planId: z.number().int(),
});

export const planAlertSseDtoSchema = z.discriminatedUnion('eventType', [
  planAlertSseBaseSchema.extend({
    eventType: z.literal('created'),
    entity: planAlertEntitySchema,
  }),
  planAlertSseBaseSchema.extend({
    eventType: z.literal('deleted'),
    entity: planAlertIdentifierSchema,
  }),
]);

export const planSseDtoSchema = z.object({
  entityType: z.literal('plan'),
  organizationId: z.string(),
  timestamp: z.string(),
  planId: z.number().int(),
  eventType: z.literal('updated_partial'),
  entity: z.object({
    id: z.number().int(),
  }),
});

export const scenarioAlertSseDtoSchema = z.discriminatedUnion('eventType', [
  z.object({
    entityType: z.literal('scenario-alert'),
    organizationId: z.string(),
    timestamp: z.string(),
    scenarioId: z.number().int(),
    eventType: z.literal('created'),
    entity: scenarioAlertEntitySchema,
  }),
  z.object({
    entityType: z.literal('scenario-alert'),
    organizationId: z.string(),
    timestamp: z.string(),
    eventType: z.literal('deleted'),
    entity: scenarioAlertIdentifierSchema,
  }),
]);

export const itemTypeSseDtoSchema = z.object({
  entityType: z.literal('item-type'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({ id: z.number().int() }).loose(),
});

export const itemTypeLevelSseDtoSchema = z.object({
  entityType: z.literal('item-type-level'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({ id: z.number().int() }).loose(),
});

export const scheduleSseDtoSchema = z.object({
  entityType: z.literal('schedule'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({ id: z.number().int() }).loose(),
});

export type ScheduleSseDto = z.output<typeof scheduleSseDtoSchema>;

const loopScheduleSseBaseSchema = z.object({
  entityType: z.literal('loop-schedule'),
  organizationId: z.string(),
  timestamp: z.string(),
});

export const loopScheduleSseDtoSchema = z.discriminatedUnion('eventType', [
  loopScheduleSseBaseSchema.extend({
    eventType: z.literal('updated_full'),
    entity: z.object({
      id: z.number().int(),
      itemId: z.number().int().optional(),
    }),
  }),
]);

export type LoopScheduleSseDto = z.output<typeof loopScheduleSseDtoSchema>;

export const itemHealthSseDtoSchema = z.object({
  entityType: z.literal('item-health'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.literal('updated_full'),
  entity: z.object({ id: z.number().int() }).loose(),
});

export type ItemHealthSseDto = z.output<typeof itemHealthSseDtoSchema>;

export const itemAggregationSseDtoSchema = z.object({
  entityType: z.literal('item-aggregation'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.literal('updated_full'),
  entity: z.object({ id: z.number().int() }).loose(),
});

export type ItemAggregationSseDto = z.output<typeof itemAggregationSseDtoSchema>;

export const dependencySseDtoSchema = z.object({
  entityType: z.literal('dependency'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.enum(['created', 'updated_full', 'deleted']),
  entity: z.object({
    predecessorId: z.number().int(),
    successorId: z.number().int(),
  }),
});

export type DependencySseDto = z.output<typeof dependencySseDtoSchema>;

export const objectiveHealthSseDtoSchema = z.object({
  entityType: z.literal('objective-health'),
  organizationId: z.string(),
  timestamp: z.string(),
  eventType: z.literal('updated_full'),
  entity: z.object({ id: z.number().int() }).loose(),
});

export type ObjectiveHealthSseDto = z.output<typeof objectiveHealthSseDtoSchema>;

export const sseDtoSchema = z.discriminatedUnion('entityType', [
  workIntegrationSseDtoSchema,
  syncStatusSseDtoSchema,
  teamSseDtoSchema,
  teamMembershipSseDtoSchema,
  holidaySchemeSseDtoSchema,
  workloadSchemeSseDtoSchema,
  membershipSseDtoSchema,
  notificationSseDtoSchema,
  scenarioSseDtoSchema,
  slackWorkspaceSseDtoSchema,
  planAlertSseDtoSchema,
  planSseDtoSchema,
  scenarioAlertSseDtoSchema,
  itemTypeSseDtoSchema,
  itemTypeLevelSseDtoSchema,
  loopScheduleSseDtoSchema,
  scheduleSseDtoSchema,
  itemHealthSseDtoSchema,
  itemAggregationSseDtoSchema,
  dependencySseDtoSchema,
  objectiveHealthSseDtoSchema,
]);

export type TeamSseDto = z.infer<typeof teamSseDtoSchema>;
export type TeamMembershipSseDto = z.infer<typeof teamMembershipSseDtoSchema>;
export type HolidaySchemeSseDto = z.infer<typeof holidaySchemeSseDtoSchema>;
export type WorkloadSchemeSseDto = z.infer<typeof workloadSchemeSseDtoSchema>;
export type ScenarioSseDto = z.infer<typeof scenarioSseDtoSchema>;
export type ScenarioAlertSseDto = z.infer<typeof scenarioAlertSseDtoSchema>;
